import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Tags, Trash2, Link2, Loader2,
  Sparkles, Database, CheckCircle2, XCircle, Clock, RefreshCw,
  Search, ChevronLeft, ChevronRight,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  useTagsetDetail, useTagsetTags, useDeleteTagset,
  useLinkTagsetToKB, useUnlinkTagsetFromKB,
} from '@/hooks/useTagsets'
import { useKnowledgeBases } from '@/hooks/useKnowledgeBases'
import { useToast } from '@/hooks/use-toast'

const statusConfig: Record<string, { label: string; icon: any; color: string; badgeCls: string }> = {
  DONE: { label: 'Ready', icon: CheckCircle2, color: 'text-green-600', badgeCls: 'bg-green-600' },
  RUNNING: { label: 'Processing', icon: RefreshCw, color: 'text-yellow-600', badgeCls: 'bg-yellow-600' },
  FAIL: { label: 'Failed', icon: XCircle, color: 'text-red-600', badgeCls: 'bg-red-600' },
  UNSTART: { label: 'Not Started', icon: Clock, color: 'text-muted-foreground', badgeCls: 'bg-muted' },
}

export function TagsetDetail() {
  const { tagsetId } = useParams<{ tagsetId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  const { data: tagset, isLoading } = useTagsetDetail(tagsetId!)
  const { data: kbData } = useKnowledgeBases({ page: 1, pageSize: 100, search: '' })
  const kbs = kbData?.items
  const deleteTagset = useDeleteTagset()
  const linkToKB = useLinkTagsetToKB()
  const unlinkFromKB = useUnlinkTagsetFromKB()

  const [selectedKbId, setSelectedKbId] = useState('')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Tags table state
  const [tagsPage, setTagsPage] = useState(1)
  const [tagsPageSize, setTagsPageSize] = useState(10)
  const [tagsSearch, setTagsSearch] = useState('')
  const [tagsSearchInput, setTagsSearchInput] = useState('')

  const { data: tagsData, isLoading: tagsLoading } = useTagsetTags(tagsetId!, {
    page: tagsPage,
    pageSize: tagsPageSize,
    search: tagsSearch,
  })

  if (isLoading || !tagset) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const status = statusConfig[tagset.status] || statusConfig.UNSTART

  const isLinked = tagset.linked_kb_count > 0
  const canDelete = !isLinked

  const handleDelete = async () => {
    try {
      await deleteTagset.mutateAsync(tagset.id)
      toast({ title: 'Tagset deleted' })
      navigate('/app/tagsets')
    } catch (err: any) {
      toast({ title: 'Delete failed', description: err?.response?.data?.error || err.message, variant: 'destructive' })
    }
  }

  const handleLink = async () => {
    if (!selectedKbId) return
    try {
      await linkToKB.mutateAsync({ tagsetId: tagset.id, kbId: selectedKbId })
      setSelectedKbId('')
      toast({ title: 'Tagset linked to knowledge base' })
    } catch (err: any) {
      toast({ title: 'Link failed', description: err?.response?.data?.error || err.message, variant: 'destructive' })
    }
  }

  const handleUnlink = async (kbId: string, kbName: string) => {
    try {
      await unlinkFromKB.mutateAsync({ tagsetId: tagset.id, kbId })
      toast({ title: `Unlinked from '${kbName}'` })
    } catch (err: any) {
      toast({ title: 'Unlink failed', description: err?.response?.data?.error || err.message, variant: 'destructive' })
    }
  }

  const availableKbs = kbs?.filter(
    (kb) => !tagset.linked_kbs.some((linked) => linked.id === kb.id)
  ) || []

  const totalTagPages = Math.ceil((tagsData?.total ?? 0) / tagsPageSize)

  // Debounced search
  const handleTagsSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setTagsSearch(tagsSearchInput)
      setTagsPage(1)
    }
  }

  return (
    <div className="space-y-6">
      {/* Top nav */}
      <Button variant="ghost" size="sm" onClick={() => navigate('/app/tagsets')}>
        <ArrowLeft className="h-4 w-4 mr-1" />
        Tagsets
      </Button>

      {/* Header row: name + stats badges + delete button */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          {tagset.source_type === 'genclair' ? (
            <Sparkles className="h-7 w-7 text-purple-500" />
          ) : (
            <Tags className="h-7 w-7 text-primary" />
          )}
          <div>
            <h1 className="text-2xl font-bold">{tagset.name}</h1>
            {tagset.description && (
              <p className="text-sm text-muted-foreground mt-0.5">{tagset.description}</p>
            )}
            {/* Compact stats as inline badges */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Badge className={status.badgeCls}>{status.label}</Badge>
              <Badge variant="outline">{tagset.tag_count} tags</Badge>
              <Badge variant="outline">{tagset.linked_kb_count} linked KB{tagset.linked_kb_count !== 1 ? 's' : ''}</Badge>
              <Badge variant="outline" className="capitalize">{tagset.source_type}</Badge>
              {tagset.source_type === 'genclair' && tagset.source_kb_name && (
                <Badge variant="secondary">
                  <Sparkles className="h-3 w-3 mr-1" />
                  from {tagset.source_kb_name}
                </Badge>
              )}
            </div>
          </div>
        </div>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={!canDelete || deleteTagset.isPending}
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  {deleteTagset.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </span>
            </TooltipTrigger>
            {!canDelete && (
              <TooltipContent>
                Unlink from all knowledge bases before deleting
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Linked KBs — compact inline section */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-muted-foreground">Linked KBs:</span>
        {tagset.linked_kbs.length > 0 ? (
          tagset.linked_kbs.map((kb) => (
            <Badge key={kb.id} variant="secondary" className="gap-1 pr-1">
              <Database className="h-3 w-3" />
              {kb.name}
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 ml-1 hover:bg-destructive/20"
                onClick={() => handleUnlink(kb.id, kb.name)}
                disabled={unlinkFromKB.isPending}
              >
                <XCircle className="h-3 w-3" />
              </Button>
            </Badge>
          ))
        ) : (
          <span className="text-sm text-muted-foreground italic">None</span>
        )}

        {/* Link new KB inline */}
        {tagset.is_ready && availableKbs.length > 0 && (
          <div className="flex items-center gap-1">
            <Select value={selectedKbId} onValueChange={setSelectedKbId}>
              <SelectTrigger className="h-7 w-[180px] text-xs">
                <SelectValue placeholder="Link a KB..." />
              </SelectTrigger>
              <SelectContent>
                {availableKbs.map((kb) => (
                  <SelectItem key={kb.id} value={kb.id}>{kb.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" className="h-7 px-2" onClick={handleLink} disabled={!selectedKbId || linkToKB.isPending}>
              {linkToKB.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />}
            </Button>
          </div>
        )}
      </div>

      {/* Tags Table */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Tags</h2>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search tags..."
                value={tagsSearchInput}
                onChange={(e) => setTagsSearchInput(e.target.value)}
                onKeyDown={handleTagsSearchKeyDown}
                onBlur={() => { setTagsSearch(tagsSearchInput); setTagsPage(1) }}
                className="pl-8 h-8 w-[200px] text-sm"
              />
            </div>
          </div>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Content</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tagsLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><div className="h-4 bg-muted animate-pulse rounded w-3/4" /></TableCell>
                  </TableRow>
                ))
              ) : (tagsData?.items?.length ?? 0) === 0 ? (
                <TableRow>
                  <TableCell className="text-center py-8 text-muted-foreground">
                    {tagset.status === 'DONE' ? 'No tags found' : 'Tagset has not been parsed yet'}
                  </TableCell>
                </TableRow>
              ) : (
                tagsData!.items.map((tag) => (
                  <TableRow key={tag.id}>
                    <TableCell className="text-sm whitespace-pre-wrap">{tag.content}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {(tagsData?.total ?? 0) > 0 && (
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <span>Rows per page</span>
              <Select value={String(tagsPageSize)} onValueChange={(v) => { setTagsPageSize(Number(v)); setTagsPage(1) }}>
                <SelectTrigger className="h-7 w-[65px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 25, 50].map((s) => (
                    <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span>
                Page {tagsPage} of {totalTagPages} ({tagsData?.total} total)
              </span>
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={tagsPage <= 1} onClick={() => setTagsPage(tagsPage - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={tagsPage >= totalTagPages} onClick={() => setTagsPage(tagsPage + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Processing indicator */}
      {tagset.status === 'RUNNING' && (
        <div className="flex items-center gap-2 text-sm text-yellow-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          Parsing in progress...
        </div>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete "{tagset.name}"?</DialogTitle>
            <DialogDescription>
              This will permanently delete the tagset. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteTagset.isPending}>
              {deleteTagset.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
